import { useState } from 'react';
import { Navbar } from '@/components/layout/Navbar';
import { Footer } from '@/components/layout/Footer';
import { EnvelopeIcon, PhoneIcon, MapPinIcon } from '@heroicons/react/24/outline';
import '@/styles/contact.css';
import { useTranslation } from 'react-i18next';
import { Link } from 'react-router-dom';

export const Contact = () => {
  const { t } = useTranslation();
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    phone: '',
    subject: '',
    message: '',
  });
  const [errors, setErrors] = useState<Record<string, string>>({});
  const [submitting, setSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(false);

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
    if (errors[name]) {
      setErrors(prev => {
        const newErrors = { ...prev };
        delete newErrors[name];
        return newErrors;
      });
    }
  };

  const validateForm = (): boolean => {
    const newErrors: Record<string, string> = {};

    if (!formData.name.trim()) {
      newErrors.name = t('contactPage.validation.nameRequired');
    }

    if (!formData.email.trim()) {
      newErrors.email = t('contactPage.validation.emailRequired');
    } else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(formData.email)) {
      newErrors.email = t('contactPage.validation.emailInvalid');
    }

    if (!formData.subject.trim()) {
      newErrors.subject = t('contactPage.validation.subjectRequired');
    }

    if (!formData.message.trim()) {
      newErrors.message = t('contactPage.validation.messageRequired');
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    if (!validateForm()) {
      return;
    }

    setSubmitting(true);
    // Simuler l'envoi du formulaire
    setTimeout(() => {
      setSubmitting(false);
      setSubmitted(true);
      setFormData({ name: '', email: '', phone: '', subject: '', message: '' });
      setTimeout(() => setSubmitted(false), 5000);
    }, 1000);
  };

  return (
    <div className="min-h-screen">
      <Navbar />
      
      <div className="contact-container">
        <div className="contact-content">
          <h4 className="gtitle contact-title">
            <span className="gtitle-light">{t('contactPage.titleLight')}</span>
            <span className="gtitle-strong">{t('contactPage.titleStrong')}</span>
          </h4>
          
          <div className="contact-grid">
            {/* Contact Information */}
            <div className="contact-info">
              <h2>{t('contactPage.infoTitle')}</h2>
              <p className="contact-description">
                {t('contactPage.description')}
              </p>

              <div className="contact-details">
                <div className="contact-detail-item">
                  <EnvelopeIcon className="contact-icon" />
                  <div>
                    <h3>{t('contactPage.email')}</h3>
                    <p>contact@hemo-j.com</p>
                  </div>
                </div>

                <div className="contact-detail-item">
                  <PhoneIcon className="contact-icon" />
                  <div>
                    <h3>{t('contactPage.phone')}</h3>
                    <p>+33 1 23 45 67 89</p>
                  </div>
                </div>

                <div className="contact-detail-item">
                  <MapPinIcon className="contact-icon" />
                  <div>
                    <h3>{t('contactPage.address')}</h3>
                    <p>123 Rue de la Santé<br />75001 Paris, France</p>
                  </div>
                </div>
              </div>

              <div className="contact-legal">
                <h3>{t('contactPage.legalHeading')}</h3>
                <p className="contact-legal-description">
                  {t('contactPage.legalIntro')}
                </p>
                <div className="contact-legal-links">
                  <Link to="/mentions-legales" className="contact-legal-link">{t('contactPage.legalLinkMentions')}</Link>
                  <Link to="/cgv" className="contact-legal-link">{t('contactPage.legalLinkCgv')}</Link>
                  <Link to="/cgv-partenaires-marketplace" className="contact-legal-link">{t('contactPage.legalLinkCgvPartners')}</Link>
                  <Link to="/rgpd" className="contact-legal-link">{t('contactPage.legalLinkRgpd')}</Link>
                  <Link to="/contrat-referencement" className="contact-legal-link">{t('contactPage.legalLinkReferencing')}</Link>
                </div>
              </div>
            </div>

            {/* Contact Form */}
            <div className="contact-form-wrapper">
              <h2>{t('contactPage.formTitle')}</h2>
              {submitted && (
                <div className="success-message">
                  {t('contactPage.success')}
                </div>
              )}
              <form onSubmit={handleSubmit} className="contact-form">
                <div className="form-group">
                  <label htmlFor="name">
                    {t('contactPage.fullName')} <span className="required">*</span>
                  </label>
                  <input
                    type="text"
                    id="name"
                    name="name"
                    value={formData.name}
                    onChange={handleInputChange}
                    className={errors.name ? 'error' : ''}
                    placeholder={t('contactPage.yourName')}
                  />
                  {errors.name && (
                    <span className="error-message">{errors.name}</span>
                  )}
                </div>

                <div className="form-row">
                  <div className="form-group">
                    <label htmlFor="email">
                      {t('contactPage.email')} <span className="required">*</span>
                    </label>
                    <input
                      type="email"
                      id="email"
                      name="email"
                      value={formData.email}
                      onChange={handleInputChange}
                      className={errors.email ? 'error' : ''}
                      placeholder={t('contactPage.emailPlaceholder')}
                    />
                    {errors.email && (
                      <span className="error-message">{errors.email}</span>
                    )}
                  </div>

                  <div className="form-group">
                    <label htmlFor="phone">{t('contactPage.phone')}</label>
                    <input
                      type="tel"
                      id="phone"
                      name="phone"
                      value={formData.phone}
                      onChange={handleInputChange}
                      placeholder={t('contactPage.phonePlaceholder')}
                    />
                  </div>
                </div>

                <div className="form-group">
                  <label htmlFor="subject">
                    {t('contactPage.subject')} <span className="required">*</span>
                  </label>
                  <select
                    id="subject"
                    name="subject"
                    value={formData.subject}
                    onChange={handleInputChange}
                    className={errors.subject ? 'error' : ''}
                  >
                    <option value="">{t('contactPage.selectSubject')}</option>
                    <option value="product">{t('contactPage.subjectProduct')}</option>
                    <option value="order">{t('contactPage.subjectOrder')}</option>
                    <option value="delivery">{t('contactPage.subjectDelivery')}</option>
                    <option value="other">{t('contactPage.subjectOther')}</option>
                  </select>
                  {errors.subject && (
                    <span className="error-message">{errors.subject}</span>
                  )}
                </div>

                <div className="form-group">
                  <label htmlFor="message">
                    {t('contactPage.message')} <span className="required">*</span>
                  </label>
                  <textarea
                    id="message"
                    name="message"
                    value={formData.message}
                    onChange={handleInputChange}
                    className={errors.message ? 'error' : ''}
                    rows={6}
                    placeholder={t('contactPage.messagePlaceholder')}
                  />
                  {errors.message && (
                    <span className="error-message">{errors.message}</span>
                  )}
                </div>

                <button
                  type="submit"
                  className="btn-submit"
                  disabled={submitting}
                >
                  {submitting ? t('contactPage.sending') : t('contactPage.send')}
                </button>
              </form>
            </div>
          </div>
        </div>
      </div>

      <Footer />
    </div>
  );
};
